import React, { Suspense, lazy } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";

import { PageShell, ToastHost } from "./components/shell";
import ErrorBoundary from "./components/ErrorBoundary";
import { ChatBot } from "./components/chatbot";
import { HomePage } from "./pages/home";

// Storefront — eager-loaded since they're small and likely first nav
import { ProductsPage } from "./pages/products-page";
import { ProductDetailPage } from "./pages/product-detail";
import { CategoriesPage, CategoryDetailPage } from "./pages/categories-pages";
import { CartPage } from "./pages/cart-page";
import { CheckoutPage } from "./pages/checkout-page";
import { ConfirmationPage } from "./pages/confirmation-page";
import { SearchPage } from "./pages/search-page";
import { ComparePage } from "./pages/compare-page";
import { TrackPage } from "./pages/track-page";
import { PolicyPage } from "./pages/policy-page";
import { OfflinePage } from "./pages/offline-page";
import { WishlistPage } from "./pages/wishlist-page";
import { NotificationsPage } from "./pages/notifications-page";

// Auth — small bundle
import { LoginPage, SignupPage, ForgotPage } from "./pages/auth-pages";

function lazyComponent(
  loader: () => Promise<any>,
  key: string
) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[key] as React.ComponentType<any> };
  });
}

// System pages — lazy
const NotFoundPage    = lazyComponent(() => import("./pages/extra-pages"), "NotFoundPage");
const GlobalErrorPage = lazyComponent(() => import("./pages/extra-pages"), "GlobalErrorPage");

// Layouts ------------------------------------------------------------

function StoreLayout({ active, children }: { active: string; children: React.ReactNode }) {
  return (
    <>
      <PageShell active={active}>
        <ErrorBoundary>{children}</ErrorBoundary>
      </PageShell>
      <ToastHost />
      <ChatBot />
    </>
  );
}

function BareLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <ErrorBoundary>{children}</ErrorBoundary>
      <ToastHost />
    </>
  );
}

function RouteFallback() {
  return (
    <div style={{ padding: 48, textAlign: "center", color: "var(--ink-3)" }}>
      Loading…
    </div>
  );
}

// Top-level scroll-to-top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname]);
  return null;
}

// Redirect /pages/foo/bar  →  /pages/foo/bar.html so clean URLs work
function UrlNormalizer() {
  const location = useLocation();
  const p = location.pathname;
  if (
    p.startsWith("/pages/") &&
    !p.endsWith(".html") &&
    !p.endsWith("/") &&
    !p.match(/\.[a-zA-Z0-9]{2,5}$/)
  ) {
    return <Navigate to={p + ".html" + location.search} replace />;
  }
  return null;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <UrlNormalizer />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Home + storefront */}
          <Route path="/"                                          element={<StoreLayout active="home"><HomePage /></StoreLayout>} />
          <Route path="/pages/storefront/products.html"            element={<StoreLayout active="products"><ProductsPage /></StoreLayout>} />
          <Route path="/pages/storefront/product.html"             element={<StoreLayout active="products"><ProductDetailPage /></StoreLayout>} />
          <Route path="/pages/storefront/categories.html"          element={<StoreLayout active="categories"><CategoriesPage /></StoreLayout>} />
          <Route path="/pages/storefront/category.html"            element={<StoreLayout active="categories"><CategoryDetailPage /></StoreLayout>} />
          <Route path="/pages/storefront/cart.html"                element={<StoreLayout active="cart"><CartPage /></StoreLayout>} />
          <Route path="/pages/storefront/checkout.html"            element={<StoreLayout active="cart"><CheckoutPage /></StoreLayout>} />
          <Route path="/pages/storefront/confirmation.html"        element={<StoreLayout active="cart"><ConfirmationPage /></StoreLayout>} />
          <Route path="/pages/storefront/search.html"              element={<StoreLayout active="search"><SearchPage /></StoreLayout>} />
          <Route path="/pages/storefront/compare.html"             element={<StoreLayout active="compare"><ComparePage /></StoreLayout>} />
          <Route path="/pages/storefront/track-order.html"         element={<StoreLayout active="track"><TrackPage /></StoreLayout>} />
          <Route path="/pages/storefront/wishlist.html"            element={<StoreLayout active="account"><WishlistPage /></StoreLayout>} />
          <Route path="/pages/storefront/notifications.html"       element={<StoreLayout active="account"><NotificationsPage /></StoreLayout>} />

          {/* Policy + system */}
          <Route path="/pages/policy/privacy.html"  element={<StoreLayout active=""><PolicyPage kind="privacy" /></StoreLayout>} />
          <Route path="/pages/policy/terms.html"    element={<StoreLayout active=""><PolicyPage kind="terms" /></StoreLayout>} />
          <Route path="/pages/policy/refund.html"   element={<StoreLayout active=""><PolicyPage kind="refund" /></StoreLayout>} />
          <Route path="/pages/system/offline.html"  element={<BareLayout><OfflinePage /></BareLayout>} />
          <Route path="/pages/system/error.html"    element={<BareLayout><GlobalErrorPage /></BareLayout>} />

          {/* Auth */}
          <Route path="/pages/auth/login.html"            element={<BareLayout><LoginPage /></BareLayout>} />
          <Route path="/pages/auth/signup.html"           element={<BareLayout><SignupPage /></BareLayout>} />
          <Route path="/pages/auth/forgot-password.html"  element={<BareLayout><ForgotPage /></BareLayout>} />

          {/* 404 */}
          <Route path="*" element={<StoreLayout active=""><NotFoundPage /></StoreLayout>} />
        </Routes>
      </Suspense>
    </>
  );
}
